import Box from "@cloudscape-design/components/box"
import ButtonDropdown from "@cloudscape-design/components/button-dropdown"
import Header from "@cloudscape-design/components/header"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { useState } from "react"
import type { CodelistItem, CodelistValue } from "@/generated/sdk"
import type { DTADocumentContent } from "../../../types/dta"
import type { InlineDiffResult } from "../../../utils/inlineDiff"
import { DTADetails } from "../../../services/dtaApi"
import { DiffBadge } from "../DiffBadge"
import { CodelistEditor } from "../shared/CodelistEditor"
import { CodelistImportWizard } from "../shared/CodelistImportWizard"
import { SDTMLibraryModal } from "../shared/SDTMLibraryModal"

interface TabContentProps {
  dta: DTADetails
  content: DTADocumentContent
  onUpdate: (updates: Partial<DTADocumentContent>) => void
  canEdit: boolean
  showErrors?: boolean
  inlineDiff?: InlineDiffResult | null
}

const MAX_CODELISTS = 20

function newCodelist(): CodelistItem {
  return {
    id: crypto.randomUUID(),
    name: "",
    column: "",
    extensible: false,
    severity: "minor",
    source: "manual",
    source_id: undefined,
    conditions: [],
    values: [],
  }
}

/**
 * Parse CSV text into CodelistValue[], auto-detecting delimiter and headers.
 */
export function parseCSV(text: string): CodelistValue[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim())
  if (lines.length < 2) return []

  // Auto-detect delimiter: tab vs comma
  const delimiter = lines[0].includes("\t") ? "\t" : ","
  const headers = lines[0].split(delimiter).map((h) =>
    h
      .trim()
      .toLowerCase()
      .replace(/^["']|["']$/g, ""),
  )

  // Map headers to indices, fall back to positional
  const codeIdx = headers.indexOf("code") !== -1 ? headers.indexOf("code") : 0
  const decodeIdx = headers.indexOf("decode") !== -1 ? headers.indexOf("decode") : 1
  const otherIdx = headers.indexOf("other_value") !== -1 ? headers.indexOf("other_value") : 2

  const values: CodelistValue[] = []
  for (let i = 1; i < lines.length && values.length < 500; i++) {
    const cols = lines[i].split(delimiter).map((c) => c.trim().replace(/^["']|["']$/g, ""))
    const code = (cols[codeIdx] || "").slice(0, 255)
    if (!code) continue
    values.push({
      code,
      decode: (cols[decodeIdx] || "").slice(0, 255),
      other_value: (cols[otherIdx] || "").slice(0, 255),
    })
  }
  return values
}

/**
 * Derive codelist name and column from filename.
 */
export function nameFromFile(filename: string): { name: string; column: string } {
  const base = filename.replace(/\.(csv|tsv|txt)$/i, "")
  return { name: base, column: base.toUpperCase().replace(/[_\s]?codelist$/i, "") }
}

export const CodelistsTab: React.FC<TabContentProps> = ({
  dta,
  content,
  onUpdate,
  canEdit,
  showErrors = false,
  inlineDiff,
}) => {
  const codeLists = content.code_lists || []
  const metadataSchema = content.transfer_metadata_schema as
    | { variables?: { variable_name?: string }[] }
    | undefined
  const columnNames = (metadataSchema?.variables || [])
    .map((v) => v.variable_name || "")
    .filter(Boolean)
  const [csvModalOpen, setCsvModalOpen] = useState(false)
  const [sdtmModalOpen, setSdtmModalOpen] = useState(false)

  const handleChange = (index: number, updated: CodelistItem) => {
    const next = [...codeLists]
    next[index] = updated
    onUpdate({ code_lists: next })
  }

  const handleRemove = (index: number) => {
    onUpdate({ code_lists: codeLists.filter((_, i) => i !== index) })
  }

  const handleAdd = () => {
    onUpdate({ code_lists: [...codeLists, newCodelist()] })
    requestAnimationFrame(() => {
      const items = document.querySelectorAll("[data-codelist-item]")
      items[items.length - 1]?.scrollIntoView({ behavior: "smooth", block: "start" })
    })
  }

  const handleCSVImport = (items: CodelistItem[]) => {
    const filtered = codeLists.filter(
      (c) => !items.some((i) => i.name.toLowerCase() === c.name.toLowerCase()),
    )
    onUpdate({ code_lists: [...filtered, ...items].slice(0, MAX_CODELISTS) })
    setTimeout(() => {
      requestAnimationFrame(() => {
        const allItems = document.querySelectorAll("[data-codelist-item]")
        allItems[allItems.length - 1]?.scrollIntoView({ behavior: "smooth", block: "start" })
      })
    }, 300)
  }

  const handleSDTMImport = (items: CodelistItem[]) => {
    const filtered = codeLists.filter(
      (c) => !items.some((i) => i.name.toLowerCase() === c.name.toLowerCase()),
    )
    onUpdate({ code_lists: [...filtered, ...items].slice(0, MAX_CODELISTS) })
    setTimeout(() => {
      requestAnimationFrame(() => {
        const allItems = document.querySelectorAll("[data-codelist-item]")
        allItems[allItems.length - 1]?.scrollIntoView({ behavior: "smooth", block: "start" })
      })
    }, 300)
  }

  return (
    <SpaceBetween size="l">
      <Header
        variant="h3"
        actions={
          canEdit && (
            <ButtonDropdown
              items={[
                { id: "blank", text: "Create blank" },
                { id: "csv", text: "Import from CSV" },
                { id: "sdtm", text: "Import from SDTM Library" },
              ]}
              onItemClick={({ detail }) => {
                if (detail.id === "blank") handleAdd()
                else if (detail.id === "csv") setCsvModalOpen(true)
                else if (detail.id === "sdtm") setSdtmModalOpen(true)
              }}
            >
              + Add Codelist
            </ButtonDropdown>
          )
        }
      />
      <CodelistImportWizard
        isOpen={csvModalOpen}
        onClose={() => setCsvModalOpen(false)}
        onImport={handleCSVImport}
        existingItems={codeLists}
      />
      <SDTMLibraryModal
        isOpen={sdtmModalOpen}
        onClose={() => setSdtmModalOpen(false)}
        onImport={handleSDTMImport}
        existingNames={codeLists.map((c) => c.name)}
      />

      {codeLists.length === 0 ? (
        <Box textAlign="center" color="text-body-secondary" padding="l">
          <SpaceBetween size="xs">
            <Box variant="p" color="text-body-secondary">
              No codelists defined yet.
            </Box>
            <Box variant="small" color="text-body-secondary">
              Add a codelist to specify which values are valid for a data column (e.g., allowed
              visit names, timepoint codes).
            </Box>
          </SpaceBetween>
        </Box>
      ) : (
        <SpaceBetween size="m">
          {codeLists.map((cl, i) => {
            const usedBy = (
              (
                content.transfer_metadata_schema as {
                  variables?: Array<{ variable_name?: string; codelist_ref?: string }>
                }
              )?.variables || []
            )
              .filter((v) => v.codelist_ref === cl.name)
              .map((v) => v.variable_name || "")
              .filter(Boolean)
            const isDuplicate =
              showErrors &&
              codeLists.some(
                (other, j) =>
                  j !== i &&
                  other.name.trim().toLowerCase() === cl.name.trim().toLowerCase() &&
                  cl.name.trim() !== "",
              )
            return (
              <div key={cl.id} data-codelist-item>
                {inlineDiff?.codelists.get(cl.name) && (
                  <DiffBadge
                    status={inlineDiff.codelists.get(cl.name)!}
                    version={dta.version}
                    dtaId={dta.dta_id}
                    section="codelists"
                    item={cl.name}
                  />
                )}
                <CodelistEditor
                  item={cl}
                  index={i}
                  onChange={handleChange}
                  onRemove={handleRemove}
                  canEdit={canEdit}
                  columnNames={columnNames}
                  dqdlPreview=""
                  showConditions={false}
                  showErrors={showErrors}
                  showColumn={false}
                  usedByColumns={usedBy}
                  duplicateName={isDuplicate}
                />
              </div>
            )
          })}
        </SpaceBetween>
      )}
    </SpaceBetween>
  )
}
